import React, { useEffect, useRef, useState } from 'react';

const normalize = (value) => String(value ?? '')
  .trim()
  .replace(/\s/g, '')
  .replace(',', '.')
  .replace(/[^0-9+\-.]/g, '');

const display = (value, digits = 2) => {
  const normalized = normalize(value);
  if (normalized === '' || normalized === '-' || normalized === '+') return '';
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return String(value ?? '');
  return numeric.toLocaleString('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  });
};

// Presentation only: keep every significant source digit, including sub-cents.
export const formatQuoteExactDecimal = (value, digits = 2) => {
  const source = String(value ?? '').replace(',', '.');
  if (!/^[+-]?\d+(\.\d*)?$/.test(source)) return source;
  const [whole, fraction = ''] = source.split('.');
  const decimals = fraction.replace(/0+$/, '').padEnd(digits, '0');
  return decimals ? `${whole},${decimals}` : whole;
};

const QuoteDecimalInput = ({
  value,
  onChange,
  digits = 2,
  disabled = false,
  min,
  max,
  className,
  ariaLabel,
  inputComponent: InputComponent,
  size,
  preservePrecision = false,
  required,
  pattern,
  id,
}) => {
  const format = preservePrecision ? formatQuoteExactDecimal : display;
  const [draft, setDraft] = useState(() => format(value, digits));
  const focused = useRef(false);
  const initialValue = useRef(value);

  useEffect(() => {
    if (!focused.current) setDraft(format(value, digits));
  }, [value, digits, format]);

  const commit = () => {
    focused.current = false;
    if (preservePrecision) {
      setDraft(format(value, digits));
      return;
    }
    const normalized = normalize(draft);
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) {
      setDraft(display(value, digits));
      return;
    }
    let bounded = numeric;
    if (min !== undefined && bounded < Number(min)) bounded = Number(min);
    if (max !== undefined && bounded > Number(max)) bounded = Number(max);
    const canonical = bounded.toFixed(digits);
    setDraft(display(canonical, digits));
    onChange?.(canonical);
  };

  const inputProps = {
    id,
    type: 'text',
    inputMode: 'decimal',
    className,
    'aria-label': ariaLabel,
    disabled,
    required,
    pattern: preservePrecision ? pattern?.replace('[.]', '[.,]') : pattern,
    value: draft,
    onFocus: () => {
      focused.current = true;
      initialValue.current = value;
      if (preservePrecision) setDraft(String(value ?? ''));
    },
    onChange: (event) => {
      setDraft(event.target.value);
      if (preservePrecision) onChange?.(event.target.value.replace(',', '.'));
    },
    onBlur: commit,
    onKeyDown: (event) => {
      if (event.key === 'Enter') event.currentTarget.blur();
      if (event.key === 'Escape') {
        if (preservePrecision) {
          onChange?.(initialValue.current);
          setDraft(format(initialValue.current, digits));
          focused.current = false;
          event.currentTarget.blur();
          return;
        }
        setDraft(display(value, digits));
        event.currentTarget.blur();
      }
    },
  };

  return InputComponent
    ? <InputComponent {...inputProps} size={size} />
    : <input {...inputProps} />;
};

export const formatQuoteDecimal = display;
export default QuoteDecimalInput;
